import { MergedReviewRow, MergedSummaryRow } from './costCenterReview.types';
import type { PeriodTableRow } from './PeriodTable';

// Figures both tabs (and the Excel export) derive from the same period responses.

// The mockup's navy table headers, and the orange of its "change" tile.
export const HEADER_BG = '#1f3b5c';
export const CHANGE_BG = '#d59b2d';
export const PER_FTE_CHART_COLOR = '#2a9d8f';
export const TREND_PERIODS = 6;
export const CATEGORY_PERIODS = 3;

// Pay group colours in the mockup's order: reds (salary), oranges/yellows (supplements,
// absence, severance), greens (benefits, social fees).
export const PAY_GROUP_COLORS = [
  '#a4373a',
  '#c0504d',
  '#d46a2a',
  '#c9975c',
  '#e0b34a',
  '#b5a33a',
  '#9fae3c',
  '#8fcc5a',
  '#5cbf2a',
  '#3c9a3c',
  '#2e7d6b',
  '#6b8fb8',
];

export interface PeriodStatus {
  period: string;
  loading: boolean;
  error: boolean;
  fte: number | null;
  unmappedAccounts: string[] | null;
}

const msekFormat = new Intl.NumberFormat('sv-SE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const formatMsek = (value: number | undefined) =>
  value === undefined ? '-' : msekFormat.format(value);

const percentFormat = new Intl.NumberFormat('sv-SE', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: 'exceptZero',
});

const shareFormat = new Intl.NumberFormat('sv-SE', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

// A signed change ratio, e.g. -0.073 -> "-7,3 %"
export const formatChange = (value: number | undefined) =>
  value === undefined ? '-' : percentFormat.format(value);

export const formatShare = (value: number | undefined) =>
  value === undefined ? '-' : shareFormat.format(value);

// (current - previous) / previous; undefined without both, or with a zero previous
export const change = (current?: number, previous?: number) =>
  current === undefined || !previous
    ? undefined
    : (current - previous) / Math.abs(previous);

// "202604" -> the month's name in `locale`, e.g. "april" (or "apr" with `short`)
export const monthName = (period: string, locale: string, short = false) => {
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(4, 6));
  if (!year || !month) return period;
  return new Intl.DateTimeFormat(locale, { month: short ? 'short' : 'long' })
    .format(new Date(year, month - 1, 1))
    .replace(/\.$/, '');
};

// Each value divided by that period's FTE; undefined without an FTE to divide by.
export const perFte = (
  amounts: Record<string, number | undefined>,
  fte: Record<string, number | null>
): Record<string, number | undefined> =>
  Object.fromEntries(
    Object.entries(amounts).map(([period, amount]) => {
      const divisor = fte[period];
      return [
        period,
        amount === undefined || !divisor ? undefined : amount / divisor,
      ];
    })
  );

export const fteByPeriodOf = (periodStatus: PeriodStatus[]) =>
  Object.fromEntries(periodStatus.map(({ period, fte }) => [period, fte]));

// Sum of the account rows per loaded period (the detail rows would count twice).
export const periodTotals = (
  rows: MergedReviewRow[],
  periodStatus: PeriodStatus[]
) => {
  const accountRows = rows.filter((row) => row.kind === 'account');
  const totals: Record<string, number> = {};
  periodStatus.forEach(({ period, loading }) => {
    if (!loading) {
      totals[period] = accountRows.reduce(
        (sum, row) => sum + (row.amounts[period] ?? 0),
        0
      );
    }
  });
  return totals;
};

export const toMsek = (amounts: Record<string, number>) =>
  Object.fromEntries(
    Object.entries(amounts).map(([period, amount]) => [period, amount / 1e6])
  );

export const summaryTable = (
  summary: MergedSummaryRow[],
  unclassifiedLabel: string
): PeriodTableRow[] =>
  summary.map((row) => ({
    key: row.key,
    label: row.key || unclassifiedLabel,
    values: row.amounts,
  }));

export const describeRow = (row: MergedReviewRow) =>
  row.kind === 'account'
    ? row.accountName
      ? `${row.account} - ${row.accountName}`
      : row.account
    : row.description ?? '';

// The KPI tab's figures per period. Periods are newest first, so each period's
// predecessor is the next one.
export const kpiFigures = (
  periodStatus: PeriodStatus[],
  rows: MergedReviewRow[]
) => {
  const periods = periodStatus.map(({ period }) => period);
  const fteByPeriod = fteByPeriodOf(periodStatus);
  const totals = periodTotals(rows, periodStatus);
  const krPerFte = perFte(totals, fteByPeriod);
  const previousOf = (index: number) => periods[index + 1];
  return {
    periods,
    loadingByPeriod: Object.fromEntries(
      periodStatus.map(({ period, loading }) => [period, loading])
    ),
    fteByPeriod,
    totals,
    msek: toMsek(totals),
    krPerFte,
    previousOf,
    krPerFteChange: (index: number) =>
      change(krPerFte[periods[index]], krPerFte[previousOf(index)]),
  };
};

export interface RollUpTable {
  key: string;
  title: string;
  rows: PeriodTableRow[];
}

// The Detaljer tab's roll-up tables below the main one, all derived from the same period
// responses. `attributeLabel` names the account attribute `byAccountingType` groups by.
export const rollUpTables = (
  rows: MergedReviewRow[],
  periodStatus: PeriodStatus[],
  byAccountingType: MergedSummaryRow[] | null,
  byPayGroup: MergedSummaryRow[],
  t: (key: string, options?: Record<string, unknown>) => string,
  attributeLabel: string | null = null
): RollUpTable[] => {
  const fteByPeriod = fteByPeriodOf(periodStatus);
  const accountTable: PeriodTableRow[] = rows
    .filter((row) => row.kind === 'account')
    .map((row) => ({
      key: row.key,
      code: row.account,
      label: describeRow(row),
      values: row.amounts,
    }));
  const totals = periodTotals(rows, periodStatus);
  const withPerFte = (table: PeriodTableRow[]) =>
    table.map((row) => ({ ...row, values: perFte(row.values, fteByPeriod) }));

  const attributeTable = byAccountingType
    ? summaryTable(byAccountingType, t('UNCLASSIFIED'))
    : null;

  return [
    ...(attributeTable
      ? [
          {
            key: 'accountingType',
            title: attributeLabel
              ? t('COST_PER_ATTRIBUTE', { attribute: attributeLabel })
              : t('COST_PER_ACCOUNTING_TYPE'),
            rows: attributeTable,
          },
          {
            key: 'accountingTypePerFte',
            title: attributeLabel
              ? t('COST_PER_ATTRIBUTE_PER_FTE', { attribute: attributeLabel })
              : t('COST_PER_ACCOUNTING_TYPE_PER_FTE'),
            rows: withPerFte(attributeTable),
          },
        ]
      : []),
    { key: 'account', title: t('COST_PER_ACCOUNT'), rows: accountTable },
    {
      key: 'accountPerFte',
      title: t('COST_PER_ACCOUNT_PER_FTE'),
      rows: withPerFte(accountTable),
    },
    {
      key: 'total',
      title: t('TOTAL_COST_PER_FTE'),
      rows: [
        {
          key: 'perFte',
          label: t('TOTAL_PER_FTE'),
          values: perFte(totals, fteByPeriod),
          bold: true,
        },
        {
          key: 'msek',
          label: t('TOTAL_MSEK'),
          values: toMsek(totals),
          format: 'msek',
        },
      ],
    },
    {
      key: 'payGroupPerFte',
      title: t('COST_PER_PAY_GROUP_PER_FTE'),
      rows: withPerFte(summaryTable(byPayGroup, t('UNCLASSIFIED'))),
    },
  ];
};
